import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const statusLabels: Record<string, string> = { TODO: 'Bekliyor', IN_PROGRESS: 'Devam Ediyor', DONE: 'Tamamlandı', CANCELLED: 'İptal' };
const priorityLabels: Record<string, string> = { LOW: 'Düşük', NORMAL: 'Normal', HIGH: 'Yüksek', URGENT: 'Acil' };

type Props = {
  task: any | null;
  countryName?: string;
  assignedToName?: string;
  onClose: () => void;
  onAddResult: (title: string, content: string) => Promise<void>;
};

const TaskDetailsDialog: React.FC<Props> = ({ task, countryName, assignedToName, onClose, onAddResult }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle('');
    setContent('');
    setError(null);
  }, [task?.id]);

  const saveResult = async () => {
    if (!content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onAddResult(title.trim(), content.trim());
      setTitle('');
      setContent('');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Sonuç eklenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(task)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{task?.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip label={statusLabels[task?.status] || task?.status} color="primary" />
            <Chip label={priorityLabels[task?.priority] || task?.priority} variant="outlined" />
          </Stack>
          <Box>
            <Typography variant="caption" color="text.secondary">Görev Açıklaması</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{task?.description || 'Açıklama yok.'}</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Box><Typography variant="caption" color="text.secondary">Atanan</Typography><Typography>{assignedToName || '—'}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Ülke</Typography><Typography>{countryName || '—'}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Son Tarih</Typography><Typography>{task?.dueAt ? new Date(task.dueAt).toLocaleString('tr-TR') : '—'}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Oluşturulma</Typography><Typography>{task?.createdAt ? new Date(task.createdAt).toLocaleString('tr-TR') : '—'}</Typography></Box>
          </Stack>
          <Divider />
          <Box>
            <Typography variant="h6" fontWeight={700}>Sonuç Geçmişi</Typography>
            <Typography variant="body2" color="text.secondary">Eklenen sonuçlar değiştirilemez veya silinemez.</Typography>
          </Box>
          <Stack spacing={1.5}>
            {task?.results?.length ? task.results.map((result: any) => (
              <Paper key={result.id} variant="outlined" sx={{ p: 2 }}>
                {result.title && <Typography fontWeight={700}>{result.title}</Typography>}
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{result.content}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {result.createdBy?.name || 'Bilinmeyen admin'} · {new Date(result.createdAt).toLocaleString('tr-TR')}
                </Typography>
              </Paper>
            )) : <Typography color="text.secondary">Henüz sonuç bilgisi eklenmedi.</Typography>}
          </Stack>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography fontWeight={700}>Yeni Sonuç Ekle</Typography>
              <TextField label="Sonuç Başlığı (isteğe bağlı)" value={title} onChange={(event) => setTitle(event.target.value)} inputProps={{ maxLength: 200 }} />
              <TextField label="Sonuç Bilgisi" value={content} onChange={(event) => setContent(event.target.value)} multiline minRows={3} inputProps={{ maxLength: 10000 }} required />
              <Button variant="contained" onClick={saveResult} disabled={!content.trim() || saving} sx={{ alignSelf: 'flex-end' }}>{saving ? 'Ekleniyor...' : 'Sonucu Ekle'}</Button>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Kapat</Button></DialogActions>
    </Dialog>
  );
};

export default TaskDetailsDialog;
